import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {Object} DeferredMediaRefs
 * @property {HTMLElement | undefined} toggleMediaButton
 * @property {HTMLElement | undefined} deferredMediaPlayButton
 *
 * @extends {Component<DeferredMediaRefs>}
 */
class DeferredMedia extends Component {
  isPlaying = false;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {boolean} isPlaying
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (!(toggleMediaButton instanceof HTMLElement)) return;

    toggleMediaButton.classList.remove('hidden');

    const playIcon = toggleMediaButton.querySelector('.icon-play');
    if (playIcon) playIcon.classList.toggle('hidden', isPlaying);

    const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
    if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
  }

  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  };

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      content.play();
    }
  }

  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe instanceof HTMLIFrameElement) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }

    this.isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  pauseMedia() {
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe instanceof HTMLIFrameElement) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }

    this.isPlaying = false;

    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(false);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}
